import { Link2 } from 'lucide-react';

import { notifySuccess } from '@/portainer/services/notifications';

import { FormControl } from '@@/form-components/FormControl';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';
import { Widget } from '@@/Widget';

import { useAllowList, useUpdateAllowListMutation, SSRFMode } from '../../queries';

import { AddEntryForm } from './AddEntryForm';
import { AllowListTable } from './AllowListTable';

const modeOptions = [
  { label: 'Off', value: SSRFMode.Off },
  { label: 'Audit', value: SSRFMode.Audit },
  { label: 'Enforce', value: SSRFMode.Enforce },
] as const;

export function SSRFPanel() {
  const allowListQuery = useAllowList();
  const mutation = useUpdateAllowListMutation();

  const allowList = allowListQuery.data;

  if (!allowList) {
    return null;
  }

  const entries = allowList.Entries ?? [];
  const mode = allowList.Mode ?? SSRFMode.Off;

  return (
    <Widget id="ssrf-config">
      <Widget.Title icon={Link2} title="SSRF" />
      <Widget.Body>
        <FormControl label="SSRF protection mode">
          <PortainerSelect
            value={mode}
            options={modeOptions}
            onChange={(value) =>
              mutation.mutate(
                {
                  ...allowList,
                  Mode: value ?? SSRFMode.Off,
                },
                {
                  onSuccess: () =>
                    notifySuccess('Success', 'SSRF mode updated'),
                }
              )
            }
            disabled={mutation.isLoading}
            data-cy="ssrf-mode-selector"
          />
        </FormControl>

        <div className="mb-3">
          <TextTip color="blue">
            Define the list of entries that are allowed for every request
            performed by the server
          </TextTip>
        </div>
        <AddEntryForm
          isLoading={mutation.isLoading}
          onSubmit={(entry) => handleSubmit([...entries, entry])}
        />

        <AllowListTable
          entries={entries}
          isLoading={mutation.isLoading}
          onDelete={(entry) => handleSubmit(entries.filter((e) => e !== entry))}
        />
      </Widget.Body>
    </Widget>
  );

  function handleSubmit(updatedEntries: string[]) {
    if (!allowList) {
      return;
    }

    mutation.mutate(
      { ...allowList, Entries: updatedEntries },
      {
        onSuccess: () => {
          notifySuccess('Success', 'allow list updated');
        },
      }
    );
  }
}
